// Pure helpers behind the "WMS ไม่ครบ" warning on /app-catalog/products
// (mdf-bo-api#583 follow-up, owner ruling 2026-10-01: "เก็บ wms_map ครึ่งเดียว
// ไว้แบบเดิม แต่ทำให้ wms_unmatched มองเห็นได้").
//
// WHAT THE WARNING IS ABOUT. A seller SKU code is a combo of warehouse codes
// joined with '+' ("SHINE50+MIXC1" = one SHINE50 and one MIXC1). When a product
// is imported from Shopee the backend resolves each segment against
// wms_product.man_pn and KEEPS WHATEVER IT FOUND — so a SKU can end up with one
// pick for a code that asks for two. Nothing else in the system shows that:
// `wms_mapped` only asks whether wms_map is non-empty, so the product badges as
// ready and the packing room under-picks every order of it, silently.
//
// --------------------------------------------------------------------------
// THE BACKEND CONTRACT — read off controllers/Catalog_product.go +
// controllers/Catalog_product_wmscoverage.go (mdf-bo-api), not off the issue:
//
//   GET /api/v1/bo/catalog/products
//   200 {"data":[{ ...,
//          "wms_unmatched_count": 0,          // ALWAYS present
//          "wms_unmatched": [                 // absent when nothing is short
//            {"sku_code":"SHINE50+MIXC1",
//             "expected": 2,                  // distinct warehouse codes wanted
//             "mapped": 1,                    // picks actually stored
//             "wms_unmatched": ["SHINE50"]}   // MAY BE EMPTY — see below
//          ]}]}
//
// `wms_unmatched_count` is the number of MISSING warehouse codes over all the
// product's partially-mapped SKUs (sum of expected-mapped), not the number of
// SKUs. It counts past the per-product detail cap of 20 SKUs, so the badge number
// can be larger than what `wms_unmatched` lists.
//
// THE NAMES CAN BE EMPTY WHILE THE COUNT IS NOT, and that is not a bug. wms_map
// stores [{wms_product_id, pickup_quantity}] and NOT the man_pn each entry came
// from, so WHICH segment failed is recoverable only from the quantities: "A+B+B"
// with one pick of quantity 2 can only be missing A, but "SHINE50+MIXC1" with one
// pick of quantity 1 is equally consistent with either. The backend refuses to
// guess there (an earlier cut did, and named the segment that had actually
// resolved), so this module renders the shortfall from expected/mapped and treats
// the names as extra detail — never as the source of the count.
// --------------------------------------------------------------------------
//
// Vue-free / axios-free on purpose: this repo's jest cannot mount a .vue file
// (see tests/unit/skuWmsOptionsSource.spec.js), so every rule worth testing lives
// here and Products.vue only renders it. Behaviour is specified in
// tests/unit/wmsCoverage.spec.js.

/** Coerce anything the API might send into a non-negative integer. */
function toCount(v) {
  const n = Number(v);
  if (!isFinite(n) || n <= 0) return 0;
  return Math.floor(n);
}

/**
 * normaliseWmsUnmatchedSkus(row) — the `wms_unmatched` array in a shape the
 * template can render without defensive checks.
 *
 * Each entry: { skuCode, expected, mapped, missing, parts, named }.
 *   missing — expected-mapped, floored at 0: the number to show for this SKU.
 *   parts   — the named warehouse codes (possibly []).
 *   named   — whether the backend could name them at all.
 *
 * Entries that carry no sku_code are dropped: a line in a tooltip that does not
 * say WHICH SKU is short is not actionable.
 */
export function normaliseWmsUnmatchedSkus(row) {
  const list = (row && row.wms_unmatched) || [];
  if (!Array.isArray(list)) return [];
  const out = [];
  list.forEach((entry) => {
    if (!entry) return;
    const skuCode = typeof entry.sku_code === 'string' ? entry.sku_code.trim() : '';
    if (!skuCode) return;
    const expected = toCount(entry.expected);
    const mapped = toCount(entry.mapped);
    const parts = Array.isArray(entry.wms_unmatched)
      ? entry.wms_unmatched.filter((p) => typeof p === 'string' && p.trim() !== '')
      : [];
    const missing = Math.max(expected - mapped, parts.length);
    out.push({
      skuCode,
      expected,
      mapped,
      missing,
      parts,
      named: parts.length > 0,
    });
  });
  return out;
}

/**
 * wmsUnmatchedCount(row) — the badge number.
 *
 * `wms_unmatched_count` is authoritative when the API sends it (it counts past
 * the detail cap). An older API that only sends the detail array, or none at all,
 * falls back to summing the entries — never to "1 if there is any", because the
 * number is the point: "WMS ไม่ครบ 3" is a different day's work from 1.
 */
export function wmsUnmatchedCount(row) {
  const direct = toCount(row && row.wms_unmatched_count);
  if (direct > 0) return direct;
  return normaliseWmsUnmatchedSkus(row).reduce((sum, e) => sum + e.missing, 0);
}

/** hasWmsUnmatched(row) — whether the row gets a badge at all. */
export function hasWmsUnmatched(row) {
  return wmsUnmatchedCount(row) > 0;
}

/**
 * filterWmsUnmatched(rows, only) — the "เฉพาะที่ WMS ไม่ครบ" toggle.
 *
 * CLIENT-SIDE, OVER THE LOADED PAGE ONLY, and the screen says so: the list
 * endpoint has no server-side filter for this, so turning the toggle on narrows
 * the 20 rows in hand rather than searching the catalog. Pretending otherwise
 * would have an operator conclude the catalog is clean after looking at page 1.
 */
export function filterWmsUnmatched(rows, only) {
  if (!Array.isArray(rows)) return [];
  if (!only) return rows;
  return rows.filter((row) => hasWmsUnmatched(row));
}

/**
 * wmsUnmatchedTooltip(row, t) — the hover text, one line per short SKU.
 *
 * `t` is vue-i18n's $t (key, params) — passed in rather than imported so this
 * module stays Vue-free and the wording stays in src/languages/*.
 *
 * Keys used (all under app_catalog):
 *   wms_unmatched_tooltip_sku    "{code}: ขาด {missing} จาก {expected} รหัสคลัง"
 *   wms_unmatched_tooltip_parts  "ไม่พบในคลัง: {parts}"
 *   wms_unmatched_tooltip_unknown "ไม่ทราบว่ารหัสไหน — เปิดหน้าแก้ไข SKU"
 *   wms_unmatched_tooltip_more   "และอีก {count} รหัส"
 *
 * The LAST line appears only when the badge counts more than the detail lists
 * (the backend caps the detail at 20 SKUs per product): a tooltip that shows 20
 * lines under a badge saying 34 must account for the other 14.
 */
export function wmsUnmatchedTooltip(row, t) {
  const translate = typeof t === 'function' ? t : (key) => key;
  const entries = normaliseWmsUnmatchedSkus(row);
  if (entries.length === 0) return '';

  const lines = entries.map((e) => {
    let line = translate('app_catalog.wms_unmatched_tooltip_sku', {
      code: e.skuCode,
      missing: e.missing,
      expected: e.expected,
    });
    if (e.named) {
      line += ' · ' + translate('app_catalog.wms_unmatched_tooltip_parts', {
        parts: e.parts.join(', '),
      });
    } else {
      line += ' · ' + translate('app_catalog.wms_unmatched_tooltip_unknown');
    }
    return line;
  });

  const shown = entries.reduce((sum, e) => sum + e.missing, 0);
  const total = wmsUnmatchedCount(row);
  if (total > shown) {
    lines.push(translate('app_catalog.wms_unmatched_tooltip_more', { count: total - shown }));
  }
  return lines.join('\n');
}

export default {
  normaliseWmsUnmatchedSkus,
  wmsUnmatchedCount,
  hasWmsUnmatched,
  filterWmsUnmatched,
  wmsUnmatchedTooltip,
};
